import { Badge } from '@react-apps/design-system';
import type {
  GrantOrigin,
  GrantedPerk,
  GrantedPerkStatus,
} from '../../domain/entities/user.entity';
import {
  describeGrantPolicyValues,
  grantPolicyLabel,
  perkTypeLabel,
} from '@/modules/catalog/presentation/labels';

/**
 * Um pacote concedido, desenhado por inteiro: status, cota, prazo, de onde veio
 * e — quando acumulou — o ledger de origens que explica um prazo que não bate
 * com nenhuma compra isolada. É o que a esquerda do editor mostra no alvo
 * usuário, onde a linha existente é um **GrantedPerk**, não um perk.
 */

const STATUS_CONFIG: Record<
  GrantedPerkStatus,
  { label: string; variant: 'default' | 'success' | 'warning' | 'error' }
> = {
  active: { label: 'Ativo', variant: 'success' },
  expired: { label: 'Expirado', variant: 'error' },
  revoked: { label: 'Revogado', variant: 'error' },
  depleted: { label: 'Esgotado', variant: 'warning' },
  suspended: { label: 'Suspenso', variant: 'warning' },
};

function statusConfig(status: GrantedPerkStatus) {
  return (
    STATUS_CONFIG[status] ?? { label: status, variant: 'default' as const }
  );
}

function formatDateTime(iso?: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** Duração aproximada em linguagem natural ("3 dias", "5 horas"). */
function formatDuration(ms: number): string {
  const days = Math.round(ms / 86_400_000);
  if (days >= 1) return `${days} ${days === 1 ? 'dia' : 'dias'}`;
  const hours = Math.round(ms / 3_600_000);
  if (hours >= 1) return `${hours} ${hours === 1 ? 'hora' : 'horas'}`;
  const minutes = Math.max(1, Math.round(ms / 60_000));
  return `${minutes} min`;
}

/** Converte um intervalo em ms num rótulo aproximado (dias/horas/minutos). */
function formatInterval(ms?: number): string | null {
  if (!ms || ms <= 0) return null;
  return `a cada ${formatDuration(ms)}`;
}

/** Rótulo de cota (uso): "3 de 10 usados · 7 restantes" quando há maxUsages. */
function formatQuota(
  snapshot: GrantedPerk['grantPolicyParamsSnapshot'],
): string | null {
  const { maxUsages, remainingUsages } = snapshot;
  if (maxUsages !== undefined) {
    const remaining = remainingUsages ?? maxUsages;
    const used = Math.max(0, maxUsages - remaining);
    return `${used} de ${maxUsages} usados · ${remaining} ${
      remaining === 1 ? 'restante' : 'restantes'
    }`;
  }
  if (remainingUsages !== undefined) {
    return `${remainingUsages} ${
      remainingUsages === 1 ? 'uso restante' : 'usos restantes'
    }`;
  }
  return null;
}

/** De onde o pacote veio, em uma linha por origem viva. */
function originLabel(origin: GrantOrigin): string {
  switch (origin.kind) {
    case 'PURCHASE':
      return `Compra · contrato ${origin.contractId}`;
    case 'MANUAL':
      return origin.presetId ? 'Concessão manual · preset' : 'Concessão manual';
    case 'SYSTEM':
      return `Sistema · ${origin.reason}`;
  }
}

const ORIGIN_STATE_LABELS: Record<
  GrantOrigin['state'],
  { label: string; variant: 'default' | 'warning' | 'error' } | null
> = {
  live: null,
  suspended: { label: 'Suspensa', variant: 'warning' },
  reverted: { label: 'Revertida', variant: 'error' },
};

interface GrantedPerkSummaryProps {
  pkg: GrantedPerk;
  /** Posição prevista de consumo entre os pacotes ativos da mesma capacidade. */
  position?: number;
  /** Nome legível da capacidade; cai na `perkKey` quando o catálogo não resolve. */
  perkName?: string;
}

export function GrantedPerkSummary({
  pkg,
  position,
  perkName,
}: GrantedPerkSummaryProps) {
  const status = statusConfig(pkg.status);
  const snapshot = pkg.grantPolicyParamsSnapshot ?? {};
  const expiresAt = formatDateTime(snapshot.expiresAt);
  const renewAt = formatDateTime(snapshot.renewAt);
  const interval = formatInterval(snapshot.renewEveryInMilliseconds);
  const quota = formatQuota(snapshot);
  const liveOrigins = pkg.origins.filter((o) => o.state === 'live');
  // Mais de uma origem viva = o pacote acumulou: janela estendeu prazo, crédito
  // somou saldo (§1.2).
  const accumulated = liveOrigins.length > 1;

  return (
    <div className="space-y-2">
      <div className="space-y-0.5">
        <span className="text-(--color-text-primary) block text-sm">
          {perkName ?? pkg.perkKey}
        </span>
        <code className="text-(--color-text-secondary) block font-mono text-xs">
          {pkg.perkKey}
        </code>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={status.variant}>{status.label}</Badge>
        <Badge variant="default">{perkTypeLabel(pkg.perkType)}</Badge>
        <Badge variant="default">{grantPolicyLabel(pkg.grantPolicyKind)}</Badge>
        {position !== undefined && (
          <Badge variant="info">{position}º a consumir</Badge>
        )}
      </div>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        <Detail
          label="Expira em"
          value={
            expiresAt
              ? accumulated
                ? `${expiresAt} (prazo estendido)`
                : expiresAt
              : 'Sem expiração'
          }
        />
        <Detail label="Cota" value={quota ?? 'Ilimitada'} />
        {(renewAt || interval) && (
          <Detail
            label="Renovação"
            value={[renewAt, interval].filter(Boolean).join(' · ')}
          />
        )}
        <Detail
          label="Origem"
          value={
            liveOrigins.length === 0
              ? 'Nenhuma origem viva'
              : liveOrigins.map((o) => originLabel(o)).join(' · ')
          }
        />
        {pkg.statusReason && (
          <Detail label="Motivo do status" value={pkg.statusReason} />
        )}
      </dl>

      {pkg.origins.length > 1 && <AccumulationTimeline origins={pkg.origins} />}
    </div>
  );
}

/**
 * O ledger de origens em ordem cronológica: cada linha é uma contribuição
 * (`applied`) que somou neste pacote.
 */
function AccumulationTimeline({ origins }: { origins: GrantOrigin[] }) {
  const ordered = [...origins].sort(
    (a, b) => new Date(a.appliedAt).getTime() - new Date(b.appliedAt).getTime(),
  );

  return (
    <details className="text-(--color-text-secondary) text-xs">
      <summary className="cursor-pointer select-none">
        Acumulação · {origins.length} origens
      </summary>
      <ol className="mt-2 space-y-1.5 border-l pl-3">
        {ordered.map((origin) => {
          const state = ORIGIN_STATE_LABELS[origin.state];
          const contribution = describeGrantPolicyValues(origin.applied);
          return (
            <li key={origin.dedupKey} className="space-y-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-(--color-text-primary)">
                  {formatDateTime(origin.appliedAt) ?? '—'}
                </span>
                <span>{originLabel(origin)}</span>
                {state && <Badge variant={state.variant}>{state.label}</Badge>}
              </div>
              <div>
                {contribution === 'Sem valores'
                  ? 'Sem contribuição de prazo ou saldo'
                  : `+ ${contribution}`}
                {origin.gapBeforeInMilliseconds > 0 &&
                  ` · reativou após ${formatDuration(
                    origin.gapBeforeInMilliseconds,
                  )} sem cobertura`}
                {origin.stateChangedAt &&
                  ` · alterada em ${formatDateTime(origin.stateChangedAt)}`}
              </div>
            </li>
          );
        })}
      </ol>
    </details>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-(--color-text-secondary) text-xs font-medium uppercase tracking-wide">
        {label}
      </dt>
      <dd className="text-(--color-text-primary) text-sm">{value}</dd>
    </div>
  );
}
